import React from 'react'
import styles from './style.module.scss'

/**
 * Import 완료 화면 (v2.3 § 17)
 *
 * Figma 기획:
 * - 체크 아이콘 + "비밀번호 가져오기가 완료되었습니다." 메시지
 * - "업로드 결과" 섹션: 세로 리스트 (업로드 성공 / 제외)
 * - 제외 0건이어도 "0건" 표시, 단 CSV 다운로드 버튼은 제외 > 0일 때만 표시
 * - 하단 "확인" 1개 버튼 (리스트 새로고침 + 모달 닫기)
 * - failCount > 0일 때만 fallback 행 추가 표시
 * - successCount === 0 && failCount > 0 → 전체 실패 화면 (빨간 ! 아이콘 + 실패 안내 텍스트)
 */
const ImportCompleteStep = ({ importResult, onDownloadExcluded, t }) => {
  const { successCount = 0, failCount = 0, excludedCount = 0 } = importResult || {}

  // 모든 레코드 import 실패 시 단순 실패 화면만 노출
  const isFailed = successCount === 0 && failCount > 0

  if (isFailed) {
    return (
      <div className={styles.completeStep}>
        <div className={styles.completeIconArea}>
          <svg
            className={styles.failIcon}
            width="50"
            height="50"
            viewBox="0 0 50 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M45 25C45 36.0457 36.0457 45 25 45C13.9543 45 5 36.0457 5 25C5 13.9543 13.9543 5 25 5C36.0457 5 45 13.9543 45 25ZM27.5 35C27.5 36.3807 26.3807 37.5 25 37.5C23.6193 37.5 22.5 36.3807 22.5 35C22.5 33.6193 23.6193 32.5 25 32.5C26.3807 32.5 27.5 33.6193 27.5 35ZM25 12.5C23.6193 12.5 22.5 13.6193 22.5 15V25C22.5 26.3807 23.6193 27.5 25 27.5C26.3807 27.5 27.5 26.3807 27.5 25V15C27.5 13.6193 26.3807 12.5 25 12.5Z"
              fill="#FD4C45"
            />
          </svg>
          <h3 className={styles.completeFailTitle}>
            {t('record.import.v2.complete.fail.title')}
            <br />
            {t('record.import.v2.complete.fail.description')}
          </h3>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.completeStep}>
      {/* 성공 아이콘 + 완료 메시지 */}
      <div className={styles.completeIconArea}>
        <div className={styles.completeIcon}>
          <svg width="28" height="22" viewBox="0 0 28 22">
            <path
              d="M2 11l8 8L26 2"
              stroke="#fff"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className={styles.completeTitle}>{t('record.import.v2.complete.title')}</h3>
      </div>

      {/* 업로드 결과 섹션 */}
      <div className={styles.completeResultSection}>
        <p className={styles.completeResultTitle}>{t('record.import.v2.complete.result')}</p>
        <ul className={styles.completeResultList}>
          <li className={styles.completeResultItem}>
            <span className={styles.completeResultLabel}>
              {t('record.import.v2.complete.uploadSuccess')}
            </span>
            <span className={styles.completeResultValueSuccess}>
              {t('record.import.v2.complete.countUnit', { count: successCount })}
            </span>
          </li>
          {/* 제외는 0건이어도 표시 (v2.3 § 32, § 17.2) */}
          <li className={styles.completeResultItem}>
            <span className={styles.completeResultLabel}>
              {t('record.import.v2.complete.excluded')}
            </span>
            <span
              className={
                excludedCount > 0
                  ? styles.completeResultValueError
                  : styles.completeResultValueMuted
              }
            >
              {t('record.import.v2.complete.countUnit', { count: excludedCount })}
            </span>
          </li>
          {/* Import 실패는 failCount > 0일 때만 fallback 표시 (Figma에 없음) */}
          {failCount > 0 && (
            <li className={styles.completeResultItem}>
              <span className={styles.completeResultLabel}>
                {t('record.import.v2.complete.failed')}
              </span>
              <span className={styles.completeResultValueError}>
                {t('record.import.v2.complete.countUnit', { count: failCount })}
              </span>
            </li>
          )}
        </ul>
      </div>

      {/* 제외 항목 CSV 다운로드 (제외 > 0일 때만) */}
      {excludedCount > 0 && (
        <button type="button" className={styles.downloadExcludedBtn} onClick={onDownloadExcluded}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M7 1v8M4 6l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M2 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {t('record.import.v2.complete.downloadExcluded')}
        </button>
      )}
    </div>
  )
}

export default ImportCompleteStep
